import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Card, 
  Typography, 
  Tag, 
  Space, 
  Button, 
  message, 
  Spin,
  Divider,
  Descriptions
} from 'antd';
import { 
  ArrowLeftOutlined, 
  EditOutlined, 
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { lawService } from '../../services/lawService';
import { attachmentService } from '../../services/attachmentService';
import { useAuth } from '../../contexts/AuthContext';
import { Law, LawCategory, LawStatus, Attachment, UserRole } from '../../types';
import { formatLocalizedDateTime, formatLocalizedDate } from '../../utils/dateUtils';
import LawVersions from './LawVersions';
import { tableStyles as generalTableStyles } from '../../utils/tableUtils';
import '../../styles/contentStyles.css';
import { removeContentEditable, setupDOMObserver, executeWithDelay } from '../../utils/domUtils';

const { Title } = Typography;

// 상세보기용 CSS 스타일 - 편집 버튼 숨김 및 표 스타일 적용
const detailStyles = `
  .preview-content .add-row-btn,
  .preview-content .delete-row-btn,
  .preview-content .table-controls {
    display: none !important;
  }
  
  .preview-content table td[contenteditable="true"] {
    cursor: default;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    pointer-events: none;
  }
  
  .preview-content table td[contenteditable="true"]:focus {
    outline: none;
    background-color: transparent;
  }
  
  .preview-content table td[contenteditable="true"]:hover {
    background-color: transparent;
  }
  
  /* 상세보기에서 모든 편집 가능 요소 비활성화 */
  .preview-content [contenteditable="true"] {
    cursor: default;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    pointer-events: none;
  }
  
  .preview-content [contenteditable="true"]:focus {
    outline: none;
    background-color: transparent;
  }
  
  /* 중첩된 Card 스타일 제거 */
  .preview-content .ant-card {
    border: none !important;
    box-shadow: none !important;
    background: transparent !important;
  }
  
  .preview-content .ant-card-body {
    padding: 0 !important;
    background: transparent !important;
  }
  
  /* 상세보기용 스타일 클래스 적용 */
  .preview-content .title-style,
  .preview-content .pub-title-style {
    font-size: 24px !important;
    font-weight: bold !important;
    text-align: center !important;
    margin: 20px 0 16px 0 !important;
    color: #262626 !important;
    display: block !important;
    width: 100% !important;
  }
  
  .preview-content .jang-style,
  .preview-content .pub-jang-style {
    font-size: 18px !important;
    font-weight: bold !important;
    text-align: left !important;
    padding: 40px 0 0 80px !important;
    margin: 0 !important;
    color: #262626 !important;
    display: block !important;
    width: 100% !important;
    border: none !important;
    background: none !important;
    outline: none !important;
  }
  
  .preview-content .jo-style,
  .preview-content .pub-jo-style {
    padding: 16px 0 0px 40px !important;
    text-indent: -40px !important;
    font-size: 16px !important;
    font-weight: normal !important;
    margin: 0 !important;
  }
  
  .preview-content .hang-style,
  .preview-content .pub-hang-style {
    padding: 0px 0 0px 60px !important;
    text-indent: -20px !important;
    font-size: 16px !important;
    font-weight: normal !important;
    margin: 0 !important;
  }
  
  .preview-content .ho-style,
  .preview-content .pub-ho-style {
    padding: 0px 0 0px 80px !important;
    text-indent: -20px !important;
    font-size: 16px !important;
    font-weight: normal !important;
    margin: 0 !important;
  }
  
  
  ${generalTableStyles}
`;

const LawDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const { t, i18n } = useTranslation();
  const [law, setLaw] = useState<Law | null>(null);
  // const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchLawDetail = useCallback(async () => {
    if (!id) return;
    
    setLoading(true);
    try {
      const data = await lawService.getLaw(id);
      setLaw(data);
      
      // 첨부파일 별도 조회 제거
      // try {
      //   const attachmentData = await attachmentService.getLawAttachments(id);
      //   setAttachments(attachmentData);
      // } catch (error) {
      //   setAttachments([]);
      // }
    } catch (error: any) {
      message.error(t('laws.detail.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    if (id) {
      fetchLawDetail();
    }
  }, [id, fetchLawDetail]);

  // 상세보기에서 contenteditable 속성 제거
  useEffect(() => {
    if (law) {
      // 즉시 실행
      removeContentEditable();
      
      // 지연 실행
      const cleanup = executeWithDelay(removeContentEditable);
      
      // MutationObserver 설정
      const previewContent = document.querySelector('.preview-content');
      const observer = previewContent ? setupDOMObserver(previewContent, removeContentEditable, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['contenteditable']
      }) : null;

      return () => {
        cleanup();
        observer?.disconnect();
      };
    }
  }, [law]);

  const handleApprove = async () => {
    if (!law) return;
    
    try {
      await lawService.approveLaw(law.law_id, { keepHistory: true });
      message.success(law.is_approved ? t('laws.approveCancelSuccess') : t('laws.approveSuccess'));
      fetchLawDetail(); // 상세 정보 새로고침
    } catch (error: any) {
      message.error(error.response?.data?.message || t('laws.approveFailed'));
    }
  };

  const handleDelete = async () => {
    if (!law) return;
    
    try {
      await lawService.deleteLaw(law.law_id);
      message.success(t('laws.deleteSuccess'));
      navigate('/laws');
    } catch (error: any) {
      message.error(error.response?.data?.message || t('laws.deleteFailed'));
    }
  };

  const handleDownloadAttachment = async (attachment: Attachment) => {
    try {
      await attachmentService.downloadAttachmentDirect(attachment.attachment_id, decodeURIComponent(attachment.file_name));
    } catch (error: any) {
      console.error('Download error:', error);
      message.error(error.message || t('laws.downloadFailed'));
    }
  };

  const getCategoryColor = (category: LawCategory) => {
    switch (category) {
      case LawCategory.KYOGYU:
        return 'blue';
      case LawCategory.KYORYEONG:
        return 'green';
      case LawCategory.YEGYU:
        return 'orange';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status: LawStatus) => {
    switch (status) {
      case LawStatus.ENACTED:
        return 'green';
      case LawStatus.AMENDED:
        return 'blue';
      case LawStatus.FULLY_AMENDED:
        return 'purple';
      case LawStatus.ABOLISHED:
        return 'red';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: LawStatus) => {
    switch (status) {
      case LawStatus.ENACTED:
        return t('laws.enacted');
      case LawStatus.AMENDED:
        return t('laws.amended');
      case LawStatus.FULLY_AMENDED:
        return t('laws.fullyAmended');
      case LawStatus.ABOLISHED:
        return t('laws.abolished');
      default:
        return status;
    }
  };

  const getCategoryText = (category: LawCategory) => {
    switch (category) {
      case LawCategory.KYOHUN: return t('laws.kyohun');
      case LawCategory.KYOGYU: return t('laws.kyogyu');
      case LawCategory.KYORYEONG: return t('laws.kyoryeong');
      case LawCategory.YEGYU: return t('laws.yegyu');
      default: return category;
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>{t('laws.DETAIL_SECTION.loading')}</div>
      </div>
    );
  }

  if (!law) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Title level={3}>{t('laws.detail.notFound')}</Title>
        <Button type="primary" onClick={() => navigate('/laws')}>
          {t('laws.detail.backToList')}
        </Button>
      </div>
    );
  }

  return (
    <div>
      <style>{detailStyles}</style>
      <Card>
        {/* 헤더 */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={() => navigate('/laws')}
            style={{ marginRight: 16 }}
          >
            {t('laws.actions.backToList')}
          </Button>
          
          <Space>
            {isAdmin && (
              <>
                <Button
                  icon={<EditOutlined />}
                  onClick={() => navigate(`/laws/${law.law_id}/edit`)}
                >
                  {law.is_approved ? t('laws.actions.amend') : t('laws.actions.edit')}
                </Button>
                <Button
                  icon={law.is_approved ? <CloseCircleOutlined /> : <CheckCircleOutlined />}
                  onClick={handleApprove}
                  type={law.is_approved ? 'default' : 'primary'}
                >
                  {law.is_approved ? t('laws.actions.cancelApprove') : t('laws.actions.approve')}
                </Button>
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => {
                    if (window.confirm(t('laws.detail.confirmDelete'))) {
                      handleDelete();
                    }
                  }}
                  disabled={law.is_approved}
                  title={law.is_approved ? t('laws.detail.approvedDeleteDisabled') : t('laws.actions.delete')}
                >
                  {t('laws.actions.delete')}
                </Button>
              </>
            )}
          </Space>
        </div>

        {/* 제목 및 메타 정보 */}
        <div style={{ marginBottom: 24 }}>
          <Title level={2}>{law.title}</Title>
          <Space size="small" style={{ marginBottom: 16 }}>
            {!isAdmin && (
              <>
                <Tag color={getStatusColor(law.status)}>
                  {law.status ? getStatusText(law.status) : '-'}
                </Tag>
                <Tag color={getCategoryColor(law.category)}>
                  {getCategoryText(law.category)}
                </Tag>
                {law.serial_number && law.serial_number !== 0 && (
                  <Tag color="default">
                    {i18n.language === 'ko' ? `제${law.serial_number}호` : `No.${law.serial_number}`}
                  </Tag>
                )}
                <Tag color="default">
                  {t('laws.detail.tags.resolutionDate')} {law.resolution_date ? formatLocalizedDate(law.resolution_date, i18n.language) : '-'}
                </Tag>
              </>
            )}
            {isAdmin && (
              <Tag color={getCategoryColor(law.category)}>
                {getCategoryText(law.category)}
              </Tag>
            )}
            {isAdmin && (
              <Tag color={law.is_approved ? 'green' : 'orange'}>
                {law.is_approved ? t('laws.detail.status.approved') : t('laws.detail.status.pending')}
              </Tag>
            )}
          </Space>
        </div>

        {/* 상세 정보 */}
        <Descriptions bordered column={2} style={{ marginBottom: 24 }}>
          {isAdmin && (
            <Descriptions.Item label={t('laws.detail.labels.author')} span={1}>
              {law.createdBy?.role === UserRole.ADMIN ? 'admin' : (law.createdBy?.bubmyeng || law.createdBy?.username || '-')}
            </Descriptions.Item>
          )}
          {isAdmin && (
            <Descriptions.Item label={t('laws.detail.labels.createdAt')} span={1}>
              {formatLocalizedDateTime(law.created_at, i18n.language)}
            </Descriptions.Item>
          )}
          {isAdmin && law.updated_at && law.updated_at !== law.created_at && (
            <Descriptions.Item label={t('laws.detail.labels.updatedAt')} span={1}>
              {formatLocalizedDateTime(law.updated_at, i18n.language)}
            </Descriptions.Item>
          )}
          {isAdmin && (
            <Descriptions.Item label={t('laws.detail.labels.documentType')}>
              <Tag color={getStatusColor(law.status)}>
                {law.status ? getStatusText(law.status) : '-'}
              </Tag>
            </Descriptions.Item>
          )}
          {isAdmin && (
            <Descriptions.Item label={t('laws.detail.labels.serialNumber')}>
              {law.serial_number && law.serial_number !== 0 ? law.serial_number : '-'}
            </Descriptions.Item>
          )}
          {isAdmin && (
            <Descriptions.Item label={t('laws.detail.labels.resolutionDate')}>
              {law.resolution_date ? formatLocalizedDate(law.resolution_date, i18n.language) : '-'}
            </Descriptions.Item>
          )}
          {isAdmin && (
            <Descriptions.Item label={t('laws.detail.labels.owner')}>
              {law.owner || '-'}
            </Descriptions.Item>
          )}
          {isAdmin && (
            <Descriptions.Item label={t('laws.detail.labels.status')}>
              <Tag color={law.is_approved ? 'green' : 'orange'}>
                {law.is_approved ? t('laws.detail.status.approved') : t('laws.detail.status.unapproved')}
              </Tag>
            </Descriptions.Item>
          )}
        </Descriptions>

        <Divider />

        {/* 첨부파일 */}
        {law && law.attachments && law.attachments.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <Title level={4}>{t('laws.detail.attachments')}</Title>
            <Card>
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                {law.attachments.map((attachment) => (
                  <div
                    key={attachment.attachment_id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '12px',
                      border: '1px solid #f0f0f0',
                      borderRadius: '6px',
                      backgroundColor: '#fafafa'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{ fontSize: '20px' }}>📎</span>
                      <div>
                        <div style={{ fontWeight: '500', marginBottom: '4px' }}>
                          {decodeURIComponent(attachment.file_name)}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          {attachmentService.formatFileSize(attachment.file_size)} • {formatLocalizedDateTime(attachment.uploaded_at, i18n.language)}
                        </div>
                      </div>
                    </div>
                    <Button
                      type="primary"
                      size="small"
                      onClick={() => handleDownloadAttachment(attachment)}
                    >
                      {t('laws.actions.download')}
                    </Button>
                  </div>
                ))}
              </Space>
            </Card>
          </div>
        )}

        {/* 내용 */}
        <div>
          <Title level={4}>{t('laws.detail.content')}</Title>
          <div 
            className="preview-content"
            style={{ 
              backgroundColor: '#fafafa',
              border: '1px solid #e8e8e8',
              borderRadius: '8px',
              padding: '20px',
              marginTop: '16px'
            }}
            dangerouslySetInnerHTML={{ __html: law.content }}
          />
        </div>
      </Card>

      {/* 버전 이력 */}
      <div style={{ marginTop: 24 }}>
        <LawVersions />
      </div>
    </div>
  );
};

export default LawDetail; 